import React, { useState, useEffect, useMemo } from "react";
import {
  CheckBox,
  FormFieldError,
  getDataAnalyticsProps,
  SortableList,
  SortableItem,
  Loader,
  useIcon,
  TextInput,
} from "@SeemplicityDev/shared";
import { difference, union, isEqual, flatMap } from "lodash-es";
import "./CheckBoxList.scss";
import ClassNames from "classnames";

/** Primitive value carried by a single checkbox option. */
type CheckBoxOptionPrimitive = string | number | boolean;

/**
 * A checkbox option's `value` may be a single primitive or an array of
 * primitives (used by the `actual_status` flow). When it is an array, the
 * first element is treated as the option's identity.
 */
export type CheckBoxOptionValue =
  | CheckBoxOptionPrimitive
  | CheckBoxOptionPrimitive[];

export interface CheckBoxListOption {
  value: CheckBoxOptionValue;
  label?: React.ReactNode;
  disabled?: boolean;
  [key: string]: unknown;
}

/**
 * Item shape consumed by the shared `SortableList`, whose generic requires an
 * `id` of `string | number` (dnd-kit's UniqueIdentifier). At runtime the option
 * identity is always a string/number even though `value` is typed more broadly.
 */
type SortableCheckBoxListItem = CheckBoxListOption & { id: string | number };

export interface CheckBoxListError {
  message: string;
  type: string;
  placement?: string;
}

export interface CheckBoxListProps {
  options?: CheckBoxListOption[];
  value?: CheckBoxOptionPrimitive[];
  onChange: (value: CheckBoxOptionPrimitive[]) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  inDropdown?: boolean;
  withBorder?: boolean;
  description?: React.ReactNode;
  labelTop?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  noMargin?: boolean;
  sortable?: boolean;
  direction?: "row" | "column";
  dateSelector?: boolean;
  error?: CheckBoxListError;
  className?: string;
  withBg?: boolean;
  "data-testid"?: string;
  loading?: boolean;
  alignLeft?: boolean;
  searchable?: boolean;
  textToHighlight?: string;
  emptyState?: React.ReactNode;
  hasSelectAll?: boolean;
  selectAllLabel?: React.ReactNode;
  // `readOnly` is accepted for backwards compatibility (was declared in the
  // original PropTypes/defaultProps) but is not consumed by the component.
  readOnly?: boolean;
  [key: string]: unknown;
}

const CheckBoxList: React.FC<CheckBoxListProps> = ({
  options,
  value,
  onChange,
  label,
  disabled = false,
  inDropdown,
  withBorder = false,
  description = "",
  labelTop = false,
  required = false,
  hideLabelHelper = false,
  noMargin,
  sortable = false,
  direction = "row",
  dateSelector,
  error,
  className,
  withBg = false,
  "data-testid": dataTestId = "",
  loading,
  alignLeft,
  searchable = false,
  textToHighlight,
  emptyState = null,
  hasSelectAll = false,
  selectAllLabel = "Select All",
  ...props
}) => {
  const [orderedOptions, setOrderedOptions] = useState<
    CheckBoxListOption[] | undefined
  >([]);
  const [valueSearchInput, setValueSearchInput] = useState("");
  const { getSvg } = useIcon();

  // we have to use effect and not initial value
  // since the options could be initially undefined or null
  useEffect(() => {
    setOrderedOptions(options);
  }, [options]);

  const handleChange = (cbValue: CheckBoxOptionValue, isChecked: boolean) => {
    let newValue: CheckBoxOptionPrimitive[] = [];

    if (Array.isArray(cbValue)) {
      //for actual_status
      if (isChecked) {
        newValue = union(value, cbValue);
      } else {
        newValue = difference(value, cbValue);
      }

      onChange(newValue);
    } else {
      const currentValue = value || [];
      if (isChecked) {
        onChange([...currentValue, cbValue]);
      } else {
        onChange(currentValue?.filter((cbVal) => cbVal !== cbValue) || []);
      }
    }
  };

  const handleSortChange = (optionsSorted: SortableCheckBoxListItem[]) => {
    setOrderedOptions(optionsSorted);
    const optionsValues = optionsSorted?.map((option) => option.value);

    const newValue = optionsValues
      ?.filter((el) => {
        if (Array.isArray(el)) {
          return value?.includes(el?.[0]);
        }
        return value?.includes(el);
      })
      ?.map((el) => {
        if (Array.isArray(el)) {
          return el?.[0];
        }

        return el;
      });

    if (!isEqual(value, newValue)) {
      // `optionsSorted` is always a defined array here, so `newValue` is never
      // undefined at runtime; the `?? []` only satisfies the optional-chaining
      // inference and preserves the original behavior.
      onChange(newValue ?? []);
    }
  };

  const displayedColumns = useMemo(() => {
    if (!!orderedOptions && searchable) {
      const list = orderedOptions.filter((c) => {
        if (typeof c?.label === "string") {
          return c.label
            ?.toLowerCase()
            ?.includes(valueSearchInput?.toLowerCase());
        } else {
          return c;
        }
      });

      return list;
    } else {
      return orderedOptions;
    }
  }, [orderedOptions, searchable, valueSearchInput]);

  const getOptionIdentity = (
    option: CheckBoxListOption
  ): CheckBoxOptionPrimitive =>
    Array.isArray(option.value) ? option.value[0] : option.value;

  const selectedDisplayedCount = (displayedColumns ?? []).filter((option) =>
    value?.includes(getOptionIdentity(option))
  ).length;
  const displayedCount = displayedColumns?.length ?? 0;
  const allDisplayedSelected =
    displayedCount > 0 && selectedDisplayedCount === displayedCount;
  const someDisplayedSelected =
    selectedDisplayedCount > 0 && selectedDisplayedCount < displayedCount;

  const handleSelectAll = (
    _selectAllValue: CheckBoxOptionValue,
    isChecked: boolean
  ) => {
    const displayedFlatValues = flatMap(displayedColumns ?? [], (option) =>
      Array.isArray(option.value) ? option.value : [option.value]
    );
    const currentValue = value ?? [];

    onChange(
      isChecked
        ? union(currentValue, displayedFlatValues)
        : difference(currentValue, displayedFlatValues)
    );
  };

  const classNameWrapper = ClassNames(
    "input-wrap",
    "checkbox-list-wrapper",
    disabled ? "is-disabled" : "",
    withBg ? "has-background" : "",
    labelTop ? "has-label-top" : "",
    inDropdown ? "in-dropdown" : "",
    alignLeft ? "has-align-left" : "",
    hideLabelHelper ? "hide-label-helper" : "",
    loading ? "is-loading" : "",
    dateSelector ? "checkbox-list-wrapper-dateselect" : "",
    !!searchable ? "is-searchable" : "",
    {
      "has-no-margin": noMargin,
    }
  );

  return (
    <div className={classNameWrapper}>
      <div className="checkbox-list-label-and-description">
        {!!label && <label className={`input-label`}>{`${label}`}</label>}
        {!!description && (
          <div className={`input-description`}>{description}</div>
        )}
      </div>
      <div
        className={`checkbox-list-search-and-options ${withBorder ? "has-border" : ""} ${error ? "has-error" : ""}`}
      >
        {loading ? (
          <Loader
            center
            className="is-spinner"
            img={getSvg("icon-spinner")}
            fullHeight
            iconSize={"xxxxl"}
          />
        ) : (
          <>
            {!!searchable && (
              <TextInput
                wrapperClassName={"checkbox-list-search"}
                data-testid={`csv-columns-search`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValueSearchInput(e.target.value);
                }}
                placeholder={"Type column name..."}
                isClearable
                leftIcon={"seem-icon-search"}
                value={valueSearchInput}
              />
            )}
            {!!displayedColumns?.length ? (
              <div
                className={`checkbox-list-options-container`}
                data-testid={`${dataTestId}-checkbox-list`}
              >
                {hasSelectAll && (
                  <div
                    className={"checkbox-list-select-all"}
                    aria-label={
                      typeof selectAllLabel === "string"
                        ? selectAllLabel
                        : "Select All"
                    }
                  >
                    <CheckBox
                      data-testid={`select-all${
                        !!dataTestId ? `-${dataTestId}` : ""
                      }`}
                      label={selectAllLabel}
                      checked={allDisplayedSelected}
                      indeterminate={someDisplayedSelected}
                      onChange={handleSelectAll}
                      disabled={disabled}
                    />
                  </div>
                )}
                <SortableList<SortableCheckBoxListItem>
                  disabled={!sortable || disabled || !!valueSearchInput}
                  onChange={handleSortChange}
                  direction={direction}
                  renderItem={(option: SortableCheckBoxListItem) => {
                    const isChecked = value?.includes(
                      Array.isArray(option.value)
                        ? option.value?.[0]
                        : option.value
                    );
                    return (
                      <SortableItem
                        id={
                          (Array.isArray(option.value)
                            ? option.value?.[0]
                            : option.value) as string | number
                        }
                      >
                        <div
                          className={`checkbox-list-option ${
                            isChecked ? "is-selected" : ""
                          }`}
                          aria-label={
                            typeof option?.label === "string"
                              ? option?.label
                              : ""
                          }
                        >
                          <CheckBox
                            data-testid={`${option.value}${
                              !!dataTestId ? `-${dataTestId}` : ""
                            }`}
                            {...option}
                            sortable={sortable}
                            className={className}
                            checked={isChecked}
                            onChange={handleChange}
                            withBg={withBg}
                            searchValue={textToHighlight || valueSearchInput}
                            disabled={disabled || option.disabled}
                            {...getDataAnalyticsProps(props)}
                          />
                        </div>
                      </SortableItem>
                    );
                  }}
                  // This branch only renders when displayedColumns has length,
                  // so the map is always defined; `|| []` satisfies the required
                  // `items` prop without changing reachable runtime behavior.
                  items={
                    displayedColumns?.map((option) => ({
                      ...option,
                      id: (Array.isArray(option.value)
                        ? option.value?.[0]
                        : option.value) as string | number,
                    })) || []
                  }
                />
              </div>
            ) : !emptyState ? (
              <Loader
                className={"no-results"}
                flexcenter
                noIconMargin
                fullHeight
                iconSize={"xxl"}
                icon={"seem-icon-filters-no-saved"}
                subtitle={`No results`}
              />
            ) : (
              emptyState
            )}
          </>
        )}
      </div>
      {error && (
        <FormFieldError
          errorMessage={error.message}
          errorType={error.type}
          // `errorPlacement` was forwarded by the original JS but is not part of
          // FormFieldErrorProps and is never read by the component (no-op prop).
        />
      )}
    </div>
  );
};

export default CheckBoxList;
